import React, { useRef, useState, useEffect } from 'react';
import { Tooltip, TooltipProps, styled, tooltipClasses } from '@mui/material';

export interface OverflowTooltipProps extends Omit<TooltipProps, 'children'> {
  children: React.ReactNode;
  forceShow?: boolean; // If true, behaves like a normal tooltip (always shows on hover)
}

/**
 * Common styled tooltip following the premium design system
 */
export const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#1D292E',
    color: '#FFFFFF',
    fontSize: '12px',
    padding: '5px 15px',
    borderRadius: '20px',
    border: '1px solid #313838',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
    fontWeight: 400,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#1D292E',
    '&:before': {
      border: '1px solid #313838',
    },
  },
}));

/**
 * A tooltip that only displays when its children (text) overflow their container.
 */
export const OverflowTooltip: React.FC<OverflowTooltipProps> = ({ 
  children, 
  title, 
  forceShow = false, 
  ...props 
}) => {
  const textElementRef = useRef<HTMLElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = () => {
    if (textElementRef.current && !forceShow) {
      const { scrollWidth, clientWidth } = textElementRef.current;
      setIsOverflowing(scrollWidth > clientWidth);
    }
  };

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [children, forceShow]);

  if (!children) {
    return null;
  }

  // Ensure we have a valid React element to clone and attach a ref to.
  // If children is a string or number, wrap it in a span.
  const elementToClone = React.isValidElement(children) 
    ? children 
    : <span>{children}</span>;

  // Clone the child to attach the ref
  const childWithRef = React.cloneElement(elementToClone as React.ReactElement, {
    ref: (node: HTMLElement) => {
      textElementRef.current = node;
    },
  });

  return (
    <CustomTooltip 
      title={title} 
      disableHoverListener={!forceShow && !isOverflowing} 
      {...props}
    >
      {childWithRef}
    </CustomTooltip>
  );
};
